/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */

import {AccessKey, ContextShape, UserRight, UserRightItem, UserRightType, UserRole} from './interfaces';

import Account from '../models/accountModel';

import {hasAdminLogin} from './adminUtil';

import {
    COMMON_SETTINGS_ROUTE,
    DASHBOARD_ROUTE,
    EMAILS_ROUTE,
    INVOICE_REQUESTS_ROUTE,
    ORDERS_ROUTE,
    PRODUCTS_ROUTE,
    RETURNS_ROUTE,
    REVIEW_ANALYTICS_ROUTE,
    SALES_DASHBOARD_ROUTE,
    SEARCH_RANKINGS_ROUTE,
    SEARCH_VOLUMES_ROUTE,
    VAT_DECLARATION_ROUTE
} from './constants';

import i18n from '../i18n';

/** True when /review-analytics renders the simplified mobile card feed. The
 *  single source for BOTH the page's layout branch (reviewAnalyticsController)
 *  and app.tsx's fluid-width gate — they must never disagree, or the feed gets
 *  the desktop 70rem width buffer (or vice versa). */
export function showsMobileReviewFeed(
    accountOrContext: Account | ContextShape | null,
    isMobileLayout: boolean
): boolean {
    return isMobileLayout && userHasRight(accountOrContext, AccessKey.PAGE_REVIEWS_FEED);
}

export function userHasRight(
    accountOrContext: Account | ContextShape | null,
    accessKey: AccessKey | Array<AccessKey>,
    accountID?: string
): boolean {
    if (hasAdminLogin()) return true;
    const account: Account | null = determineAccount(accountOrContext, accountID);
    if (!account) return false;
    if (account.isAccountOwner()) return true;
    const rights = account.userRights;
    const accessKeys = Array.isArray(accessKey) ? accessKey : [accessKey];
    for (const right of rights) {
        if (!accessKeys.includes(right.key)) continue;
        if (right.allow) return true;
    }
    return false;
}

function determineAccount(
    accountOrContext: Account | ContextShape | null,
    accountID: string | undefined
): Account | null {
    if (!accountOrContext) return null;
    if (accountOrContext instanceof Account) return accountOrContext;
    if (!accountID) return accountOrContext.accountContext.account;
    return accountOrContext.accountContext.accounts.find((account: Account) => account.id === accountID) ?? null;
}

export function translateUserRole(role: UserRole | null): string {
    switch (role) {
        case UserRole.ADMIN:
            return i18n.t('util.access.role.admin', 'Administrator');
        case UserRole.EMPLOYEE:
            return i18n.t('util.access.role.employee', 'Medewerker');
        case UserRole.OWNER:
            return i18n.t('util.access.role.owner', 'Eigenaar');
        case UserRole.ACCOUNTANT:
            return i18n.t('util.access.role.accountant', 'Boekhouder');
        case UserRole.CUSTOMER_SERVICE:
            return i18n.t('util.access.role.customerService', 'Klantenservice');
        case null:
            return '';
    }
}

export function getDefaultRoleRights(role: UserRole): Array<UserRight> {
    const rights = DEFAULT_ROLE_RIGHTS[role];
    if (!rights) return [];
    return rights.map((key: AccessKey) => ({key: key, right_type: UserRightType.ACCESS, allow: true}));
}

/** Where the app sends a user when it has no specific destination: after login, on
 *  an unknown URL, and when a page is refused. Owners land on the dashboard; sub-users
 *  walk the rights ladder to the first page they may open, with the (paywall-exempt)
 *  settings page as the final fallback. */
export function getDefaultRoute(rights?: Array<UserRight>, userRole?: UserRole) {
    if (userRole === UserRole.OWNER) return DASHBOARD_ROUTE;
    if (findRight(rights, AccessKey.PAGE_DASHBOARD)?.allow) return DASHBOARD_ROUTE;
    if (findRight(rights, AccessKey.PAGE_INVOICES)?.allow) return ORDERS_ROUTE;
    if (findRight(rights, AccessKey.PAGE_INVOICE_REQUESTS)?.allow) return INVOICE_REQUESTS_ROUTE;
    if (findRight(rights, AccessKey.PAGE_RETURNS)?.allow) return RETURNS_ROUTE;
    if (findRight(rights, AccessKey.PAGE_EMAIL)?.allow) return EMAILS_ROUTE;
    if (findRight(rights, AccessKey.PAGE_SALES_DASHBOARD)?.allow) return SALES_DASHBOARD_ROUTE;
    if (findRight(rights, [AccessKey.PAGE_REVIEWS_PRODUCTS, AccessKey.PAGE_REVIEWS_FEED])?.allow)
        return REVIEW_ANALYTICS_ROUTE;
    if (findRight(rights, AccessKey.PAGE_VAT_DECLARATION)?.allow) return VAT_DECLARATION_ROUTE;
    if (findRight(rights, AccessKey.PAGE_SEARCH_TERMS)?.allow) return SEARCH_VOLUMES_ROUTE;
    if (findRight(rights, AccessKey.PAGE_SEARCH_RANKINGS)?.allow) return SEARCH_RANKINGS_ROUTE;
    if (findRight(rights, AccessKey.PAGE_PRODUCTS)?.allow) return PRODUCTS_ROUTE;
    return COMMON_SETTINGS_ROUTE;
}

function findRight(
    rights: Array<UserRight> | undefined,
    accessKey: AccessKey | Array<AccessKey>
): UserRight | undefined {
    if (!rights) return undefined;
    const accessKeys = Array.isArray(accessKey) ? accessKey : [accessKey];
    return rights.find((right: UserRight) => accessKeys.includes(right.key) && right.allow);
}

export const DEFAULT_ROLE_RIGHTS: {[key in UserRole]: Array<AccessKey>} = {
    [UserRole.OWNER]: [],
    [UserRole.ACCOUNTANT]: [
        AccessKey.PAGE_DASHBOARD,
        AccessKey.PAGE_INVOICES,
        AccessKey.PAGE_ORDERS,
        AccessKey.PAGE_RETURNS,
        AccessKey.PAGE_SALES_DASHBOARD,
        AccessKey.PAGE_VAT_DECLARATION,
        AccessKey.PAGE_SETTINGS_PRODUCTS,
        AccessKey.PAGE_SETTINGS_EXPORT,
        AccessKey.PAGE_SETTINGS_SUBSCRIPTION,
        AccessKey.PAGE_PRODUCTS,
        AccessKey.INVOICE_DETAILS,
        AccessKey.INVOICE_DOWNLOAD,
        AccessKey.INVOICE_BULK_DOWNLOAD,
        AccessKey.INVOICE_STATS,
        AccessKey.INVOICE_CREDIT,
        AccessKey.INVOICE_REVERSE_CHARGE,
        AccessKey.RETURN_DETAILS,
        AccessKey.SALES_DASHBOARD_EXPORT,
        AccessKey.SALES_DASHBOARD_COST_PRICES,
        AccessKey.VAT_DECLARATION_EXPORT,
        AccessKey.VAT_DECLARATION_DOWNLOAD_SPEC,
        AccessKey.PRODUCT_SETTINGS_EDIT_VAT,
        AccessKey.PRODUCT_SETTINGS_UPLOAD,
        AccessKey.EXPORT_SETTINGS_DOWNLOAD,
        AccessKey.EXPORT_SETTINGS_EXPORT
    ],
    [UserRole.CUSTOMER_SERVICE]: [
        AccessKey.PAGE_DASHBOARD,
        AccessKey.PAGE_INVOICES,
        AccessKey.PAGE_ORDERS,
        AccessKey.PAGE_INVOICE_REQUESTS,
        AccessKey.PAGE_RETURNS,
        AccessKey.INVOICE_DETAILS,
        AccessKey.INVOICE_EDIT_CUSTOMER,
        AccessKey.INVOICE_EDIT_ITEMS,
        AccessKey.INVOICE_DOWNLOAD,
        AccessKey.INVOICE_SEND,
        AccessKey.INVOICE_CREDIT,
        AccessKey.INVOICE_REVERSE_CHARGE,
        AccessKey.INVOICE_INVOICE_NUMBER,
        AccessKey.INVOICE_REQUEST_UPLOAD,
        AccessKey.INVOICE_REQUEST_ARCHIVE,
        AccessKey.RETURN_DETAILS,
        AccessKey.RETURN_CREATE_CREDIT,
        AccessKey.RETURN_SEND_CREDIT,
        AccessKey.RETURN_BOL
    ],
    [UserRole.EMPLOYEE]: [
        AccessKey.PAGE_DASHBOARD,
        AccessKey.PAGE_INVOICES,
        AccessKey.PAGE_ORDERS,
        AccessKey.PAGE_INVOICE_REQUESTS,
        AccessKey.PAGE_RETURNS,
        AccessKey.PAGE_INVOICE_LAYOUT,
        AccessKey.PAGE_EMAIL,
        AccessKey.PAGE_NOTIFICATIONS,
        AccessKey.PAGE_SALES_DASHBOARD,
        AccessKey.PAGE_REVIEWS_PRODUCTS,
        AccessKey.PAGE_REVIEWS_FEED,
        AccessKey.PAGE_REVIEWS_SETTINGS,
        AccessKey.PAGE_REVIEWS_EXPORT,
        AccessKey.PAGE_VAT_DECLARATION,
        AccessKey.PAGE_SEARCH_TERMS,
        AccessKey.PAGE_SETTINGS_API,
        AccessKey.PAGE_SETTINGS_PRODUCTS,
        AccessKey.PAGE_SETTINGS_INVOICE,
        AccessKey.PAGE_SETTINGS_EMAILS,
        AccessKey.PAGE_SETTINGS_EXPORT,
        AccessKey.PAGE_PRODUCTS,
        AccessKey.PAGE_SEARCH_RANKINGS,
        AccessKey.INVOICE_DETAILS,
        AccessKey.INVOICE_STATS,
        AccessKey.INVOICE_BULK_DOWNLOAD,
        AccessKey.INVOICE_EDIT_CUSTOMER,
        AccessKey.INVOICE_EDIT_ITEMS,
        AccessKey.INVOICE_DOWNLOAD,
        AccessKey.INVOICE_SEND,
        AccessKey.INVOICE_CREDIT,
        AccessKey.INVOICE_REVERSE_CHARGE,
        AccessKey.INVOICE_CANCEL_EMAILS,
        AccessKey.INVOICE_INVOICE_NUMBER,
        AccessKey.INVOICE_REQUEST_UPLOAD,
        AccessKey.INVOICE_REQUEST_SETTINGS,
        AccessKey.INVOICE_REQUEST_ARCHIVE,
        AccessKey.INVOICE_REQUEST_STATS,
        AccessKey.RETURN_DETAILS,
        AccessKey.RETURN_BOL,
        AccessKey.RETURN_SEND_CREDIT,
        AccessKey.RETURN_CREATE_CREDIT,
        AccessKey.EMAIL_EDIT,
        AccessKey.EMAIL_STATS,
        AccessKey.EMAIL_EDIT_AUTOMATIC,
        AccessKey.EMAIL_TEST_MAIl,
        AccessKey.EMAIL_COPY,
        AccessKey.NOTIFICATIONS_EDIT,
        AccessKey.NOTIFICATIONS_DELETE,
        AccessKey.SALES_DASHBOARD_COST_PRICES,
        AccessKey.SALES_DASHBOARD_EXPORT,
        AccessKey.RATING_VIEW,
        AccessKey.RATING_SETTINGS,
        AccessKey.RATING_CREATE_EXPORT,
        AccessKey.RATING_DOWNLOAD_EXPORT,
        AccessKey.VAT_DECLARATION_EXPORT,
        AccessKey.VAT_DECLARATION_DOWNLOAD_SPEC,
        AccessKey.SETTING_EDIT_COMPANY,
        AccessKey.API_SETTINGS_DISCONNECT,
        AccessKey.API_SETTINGS_SETUP,
        AccessKey.PRODUCT_SETTINGS_EDIT_TITLE,
        AccessKey.PRODUCT_SETTINGS_EDIT_VAT,
        AccessKey.PRODUCT_SETTINGS_UPLOAD,
        AccessKey.PRODUCT_SETTINGS_SYNC,
        AccessKey.INVOICE_SETTINGS_EDIT,
        AccessKey.EMAIL_SETTINGS_EDIT,
        AccessKey.EXPORT_SETTINGS_EXPORT,
        AccessKey.EXPORT_SETTINGS_DOWNLOAD
    ],
    [UserRole.ADMIN]: [
        AccessKey.PAGE_DASHBOARD,
        AccessKey.PAGE_INVOICES,
        AccessKey.PAGE_ORDERS,
        AccessKey.PAGE_INVOICE_REQUESTS,
        AccessKey.PAGE_RETURNS,
        AccessKey.PAGE_INVOICE_LAYOUT,
        AccessKey.PAGE_EMAIL,
        AccessKey.PAGE_NOTIFICATIONS,
        AccessKey.PAGE_SALES_DASHBOARD,
        AccessKey.PAGE_REVIEWS_PRODUCTS,
        AccessKey.PAGE_REVIEWS_FEED,
        AccessKey.PAGE_REVIEWS_SETTINGS,
        AccessKey.PAGE_REVIEWS_EXPORT,
        AccessKey.PAGE_VAT_DECLARATION,
        AccessKey.PAGE_SEARCH_TERMS,
        AccessKey.PAGE_ACCOUNT_USERS,
        AccessKey.PAGE_SETTINGS_API,
        AccessKey.PAGE_SETTINGS_PRODUCTS,
        AccessKey.PAGE_SETTINGS_INVOICE,
        AccessKey.PAGE_SETTINGS_EMAILS,
        AccessKey.PAGE_SETTINGS_EXPORT,
        AccessKey.PAGE_PRODUCTS,
        AccessKey.PAGE_SEARCH_RANKINGS,
        AccessKey.INVOICE_DETAILS,
        AccessKey.INVOICE_STATS,
        AccessKey.INVOICE_BULK_DOWNLOAD,
        AccessKey.INVOICE_EDIT_CUSTOMER,
        AccessKey.INVOICE_EDIT_ITEMS,
        AccessKey.INVOICE_DOWNLOAD,
        AccessKey.INVOICE_SEND,
        AccessKey.INVOICE_CREDIT,
        AccessKey.INVOICE_REVERSE_CHARGE,
        AccessKey.INVOICE_CANCEL_EMAILS,
        AccessKey.INVOICE_INVOICE_NUMBER,
        AccessKey.INVOICE_REQUEST_UPLOAD,
        AccessKey.INVOICE_REQUEST_SETTINGS,
        AccessKey.INVOICE_REQUEST_ARCHIVE,
        AccessKey.INVOICE_REQUEST_STATS,
        AccessKey.RETURN_DETAILS,
        AccessKey.RETURN_BOL,
        AccessKey.RETURN_SEND_CREDIT,
        AccessKey.RETURN_CREATE_CREDIT,
        AccessKey.EMAIL_EDIT,
        AccessKey.EMAIL_STATS,
        AccessKey.EMAIL_EDIT_AUTOMATIC,
        AccessKey.EMAIL_TEST_MAIl,
        AccessKey.EMAIL_COPY,
        AccessKey.NOTIFICATIONS_EDIT,
        AccessKey.NOTIFICATIONS_DELETE,
        AccessKey.SALES_DASHBOARD_COST_PRICES,
        AccessKey.SALES_DASHBOARD_EXPORT,
        AccessKey.RATING_VIEW,
        AccessKey.RATING_SETTINGS,
        AccessKey.RATING_CREATE_EXPORT,
        AccessKey.RATING_DOWNLOAD_EXPORT,
        AccessKey.VAT_DECLARATION_EXPORT,
        AccessKey.VAT_DECLARATION_DOWNLOAD_SPEC,
        AccessKey.SETTING_EDIT_COMPANY,
        AccessKey.SETTINGS_DELETE_COMPANY,
        AccessKey.API_SETTINGS_DISCONNECT,
        AccessKey.API_SETTINGS_SETUP,
        AccessKey.PRODUCT_SETTINGS_EDIT_TITLE,
        AccessKey.PRODUCT_SETTINGS_EDIT_VAT,
        AccessKey.PRODUCT_SETTINGS_UPLOAD,
        AccessKey.PRODUCT_SETTINGS_SYNC,
        AccessKey.INVOICE_SETTINGS_EDIT,
        AccessKey.EMAIL_SETTINGS_EDIT,
        AccessKey.EXPORT_SETTINGS_EXPORT,
        AccessKey.EXPORT_SETTINGS_DOWNLOAD,
        AccessKey.ACCOUNT_USERS_INVITE,
        AccessKey.ACCOUNT_USERS_EDIT
    ]
};

export const getAvailableUserRights = (): Record<string, Array<UserRightItem>> => ({
    [i18n.t('util.access.tab.pageAccess', "Toegang tot pagina's")]: [
        {
            label: i18n.t('util.access.rights.dashboardPage', 'Home pagina'),
            key: AccessKey.PAGE_DASHBOARD,
            right_type: UserRightType.ACCESS
        },
        {
            label: i18n.t('util.access.rights.invoices', 'Facturen'),
            key: AccessKey.PAGE_INVOICES,
            right_type: UserRightType.ACCESS,
            children: [
                {
                    label: i18n.t('util.access.rights.ordersPage', 'Orders pagina'),
                    key: AccessKey.PAGE_ORDERS,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.invoiceRequestsPage', 'Aanvragen pagina'),
                    key: AccessKey.PAGE_INVOICE_REQUESTS,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.returnsPage', 'Retouren pagina'),
                    key: AccessKey.PAGE_RETURNS,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.layoutPage', 'Layout pagina'),
                    key: AccessKey.PAGE_INVOICE_LAYOUT,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.searchRankingsPage', 'Search Rankings pagina'),
                    key: AccessKey.PAGE_SEARCH_RANKINGS,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.productsPage', 'Producten pagina'),
                    key: AccessKey.PAGE_PRODUCTS,
                    right_type: UserRightType.ACCESS
                }
            ]
        },
        {
            label: i18n.t('util.access.rights.emailsPage', 'E-mails pagina'),
            key: AccessKey.PAGE_EMAIL,
            right_type: UserRightType.ACCESS
        },
        {
            label: i18n.t('util.access.rights.notificationsPage', 'Notificaties pagina'),
            key: AccessKey.PAGE_NOTIFICATIONS,
            right_type: UserRightType.ACCESS
        },
        {
            label: i18n.t('util.access.rights.salesDashboardPage', 'Sales Dashboard pagina'),
            key: AccessKey.PAGE_SALES_DASHBOARD,
            right_type: UserRightType.ACCESS
        },
        {
            label: i18n.t('util.access.rights.reviewAnalyticsPage', 'Review Inzicht pagina'),
            key: AccessKey.PAGE_REVIEW_ANALYTICS,
            right_type: UserRightType.ACCESS,
            children: [
                {
                    label: i18n.t('util.access.rights.productsTab', 'Producten tab'),
                    key: AccessKey.PAGE_REVIEWS_PRODUCTS,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.feedTab', 'Feed tab'),
                    key: AccessKey.PAGE_REVIEWS_FEED,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.settingsTab', 'Instellingen tab'),
                    key: AccessKey.PAGE_REVIEWS_SETTINGS,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.exportTab', 'Exporteren tab'),
                    key: AccessKey.PAGE_REVIEWS_EXPORT,
                    right_type: UserRightType.ACCESS
                }
            ]
        },
        {
            label: i18n.t('util.access.rights.vatDeclarationPage', 'BTW Aangifte pagina'),
            key: AccessKey.PAGE_VAT_DECLARATION,
            right_type: UserRightType.ACCESS
        },
        {
            label: i18n.t('util.access.rights.searchTermsPage', 'Zoek Trends pagina'),
            key: AccessKey.PAGE_SEARCH_TERMS,
            right_type: UserRightType.ACCESS
        },
        {
            label: i18n.t('util.access.rights.settingsPage', 'Instellingen pagina'),
            key: AccessKey.PAGE_SETTINGS,
            right_type: UserRightType.ACCESS,
            children: [
                {
                    label: i18n.t('util.access.rights.accountUsers', 'Gebruikersbeheer'),
                    key: AccessKey.PAGE_ACCOUNT_USERS,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.apiTab', 'Koppeling Bol tab'),
                    key: AccessKey.PAGE_SETTINGS_API,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.productsVatTab', 'Producten (BTW) tab'),
                    key: AccessKey.PAGE_SETTINGS_PRODUCTS,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.invoiceSettingsTab', 'Factuurinstellingen tab'),
                    key: AccessKey.PAGE_SETTINGS_INVOICE,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.emailsTab', 'E-mails tab'),
                    key: AccessKey.PAGE_SETTINGS_EMAILS,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.exportTab', 'Exporteren tab'),
                    key: AccessKey.PAGE_SETTINGS_EXPORT,
                    right_type: UserRightType.ACCESS
                },
                {
                    label: i18n.t('util.access.rights.subscriptionTab', 'Abonnement tab'),
                    key: AccessKey.PAGE_SETTINGS_SUBSCRIPTION,
                    right_type: UserRightType.ACCESS
                }
            ]
        }
    ],
    [i18n.t('util.access.tab.orders', 'Orders')]: [
        {
            label: i18n.t('util.access.rights.invoices', 'Facturen'),
            key: AccessKey.INVOICE,
            right_type: UserRightType.ACTION,
            children: [
                {
                    label: i18n.t('util.access.rights.viewDetails', 'Details inzien'),
                    key: AccessKey.INVOICE_DETAILS,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.viewOrderStats', 'Order statistieken inzien'),
                    key: AccessKey.INVOICE_STATS,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.editCustomer', 'Klantgegevens wijzigen'),
                    key: AccessKey.INVOICE_EDIT_CUSTOMER,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.editItems', 'Factuurregels wijzigen'),
                    key: AccessKey.INVOICE_EDIT_ITEMS,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.downloadPreview', 'Downloaden/Voorbeeld'),
                    key: AccessKey.INVOICE_DOWNLOAD,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.bulkDownload', 'Bulk downloaden'),
                    key: AccessKey.INVOICE_BULK_DOWNLOAD,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.send', 'Verzenden'),
                    key: AccessKey.INVOICE_SEND,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.credit', 'Crediteren'),
                    key: AccessKey.INVOICE_CREDIT,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.reverseCharge', 'BTW verleggen'),
                    key: AccessKey.INVOICE_REVERSE_CHARGE,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.cancelEmails', 'Mails annuleren'),
                    key: AccessKey.INVOICE_CANCEL_EMAILS,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.setInvoiceNumber', 'Factuurnummer instellen'),
                    key: AccessKey.INVOICE_INVOICE_NUMBER,
                    right_type: UserRightType.ACTION
                }
            ]
        },
        {
            label: i18n.t('util.access.rights.invoiceRequests', 'Aanvragen'),
            key: AccessKey.INVOICE_REQUESTS,
            right_type: UserRightType.ACTION,
            children: [
                {
                    label: i18n.t('util.access.rights.viewStats', 'Statistieken inzien'),
                    key: AccessKey.INVOICE_REQUEST_STATS,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.uploadInvoices', 'Facturen uploaden'),
                    key: AccessKey.INVOICE_REQUEST_UPLOAD,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.archiveRequests', 'Aanvragen archiveren'),
                    key: AccessKey.INVOICE_REQUEST_ARCHIVE,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.requestSettings', 'Aanvragen instellingen'),
                    key: AccessKey.INVOICE_REQUEST_SETTINGS,
                    right_type: UserRightType.ACCESS
                }
            ]
        },
        {
            label: i18n.t('util.access.rights.returns', 'Retouren'),
            key: AccessKey.RETURN,
            right_type: UserRightType.ACTION,
            children: [
                {
                    label: i18n.t('util.access.rights.viewDetails', 'Details inzien'),
                    key: AccessKey.RETURN_DETAILS,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.showInBol', 'Weergeven in Bol'),
                    key: AccessKey.RETURN_BOL,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.sendCreditInvoice', 'Creditfactuur verzenden'),
                    key: AccessKey.RETURN_SEND_CREDIT,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.createCreditInvoice', 'Creditfactuur aanmaken'),
                    key: AccessKey.RETURN_CREATE_CREDIT,
                    right_type: UserRightType.ACTION
                }
            ]
        }
    ],
    [i18n.t('util.access.tab.emails', 'E-mails')]: [
        {
            label: i18n.t('util.access.rights.editEmails', 'E-mails wijzigen'),
            key: AccessKey.EMAIL_EDIT,
            right_type: UserRightType.ACTION
        },
        {
            label: i18n.t('util.access.rights.viewStats', 'Statistieken inzien'),
            key: AccessKey.EMAIL_STATS,
            right_type: UserRightType.ACTION
        },
        {
            label: i18n.t('util.access.rights.editAutoEmail', 'Automatisch mailen wijzigen'),
            key: AccessKey.EMAIL_EDIT_AUTOMATIC,
            right_type: UserRightType.ACTION
        },
        {
            label: i18n.t('util.access.rights.sendTestMail', 'Testmail verzenden'),
            key: AccessKey.EMAIL_TEST_MAIl,
            right_type: UserRightType.ACTION
        },
        {
            label: i18n.t('util.access.rights.copyEmail', 'E-mail kopiëren van ander account'),
            key: AccessKey.EMAIL_COPY,
            right_type: UserRightType.ACTION
        }
    ],
    [i18n.t('util.access.tab.notifications', 'Notificaties')]: [
        {
            label: i18n.t('util.access.rights.editNotifications', 'Notificaties wijzigen'),
            key: AccessKey.NOTIFICATIONS_EDIT,
            right_type: UserRightType.ACTION
        },
        {
            label: i18n.t('util.access.rights.deleteNotifications', 'Notificaties verwijderen'),
            key: AccessKey.NOTIFICATIONS_DELETE,
            right_type: UserRightType.ACTION
        }
    ],
    [i18n.t('util.access.tab.salesDashboard', 'Sales Dashboard')]: [
        {
            label: i18n.t('util.access.rights.setProductCosts', 'Productkosten instellen'),
            key: AccessKey.SALES_DASHBOARD_COST_PRICES,
            right_type: UserRightType.ACTION
        },
        {
            label: i18n.t('util.access.rights.export', 'Exporteren'),
            key: AccessKey.SALES_DASHBOARD_EXPORT,
            right_type: UserRightType.ACTION
        }
    ],
    [i18n.t('util.access.tab.reviewAnalytics', 'Review Inzicht')]: [
        {
            label: i18n.t('util.access.rights.viewReviews', 'Reviews inzien'),
            key: AccessKey.RATING_VIEW,
            right_type: UserRightType.ACTION
        },
        {
            label: i18n.t('util.access.rights.editSettings', 'Instellingen wijzigen'),
            key: AccessKey.RATING_SETTINGS,
            right_type: UserRightType.ACTION
        },
        {
            label: i18n.t('util.access.rights.createExport', 'Export maken'),
            key: AccessKey.RATING_CREATE_EXPORT,
            right_type: UserRightType.ACTION
        },
        {
            label: i18n.t('util.access.rights.downloadExport', 'Export downloaden'),
            key: AccessKey.RATING_DOWNLOAD_EXPORT,
            right_type: UserRightType.ACTION
        }
    ],
    [i18n.t('util.access.tab.vatDeclaration', 'BTW Aangifte')]: [
        {
            label: i18n.t('util.access.rights.exportCalculation', 'Berekening exporteren'),
            key: AccessKey.VAT_DECLARATION_EXPORT,
            right_type: UserRightType.ACTION
        },
        {
            label: i18n.t('util.access.rights.downloadInvoiceSpec', 'Factuurspecificaties downloaden'),
            key: AccessKey.VAT_DECLARATION_DOWNLOAD_SPEC,
            right_type: UserRightType.ACTION
        }
    ],
    [i18n.t('util.access.tab.settings', 'Instellingen')]: [
        {
            label: i18n.t('util.access.rights.general', 'Algemeen'),
            key: AccessKey.SETTINGS_COMMON,
            right_type: UserRightType.ACTION,
            children: [
                {
                    label: i18n.t('util.access.rights.editCompany', 'Bedrijfsgegevens wijzigen'),
                    key: AccessKey.SETTING_EDIT_COMPANY,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.deleteAccount', 'Account verwijderen'),
                    key: AccessKey.SETTINGS_DELETE_COMPANY,
                    right_type: UserRightType.ACTION
                }
            ]
        },
        {
            label: i18n.t('util.access.rights.users', 'Gebruikers'),
            key: AccessKey.SETTINGS_ACCOUNT_USERS,
            right_type: UserRightType.ACTION,
            children: [
                {
                    label: i18n.t('util.access.rights.inviteUsers', 'Gebruikers toevoegen'),
                    key: AccessKey.ACCOUNT_USERS_INVITE,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.editUsers', 'Gebruikers wijzigen'),
                    key: AccessKey.ACCOUNT_USERS_EDIT,
                    right_type: UserRightType.ACTION
                }
            ]
        },
        {
            label: i18n.t('util.access.rights.bolConnection', 'Koppeling Bol'),
            key: AccessKey.API_SETTINGS,
            right_type: UserRightType.ACTION,
            children: [
                {
                    label: i18n.t('util.access.rights.disconnectApi', 'Koppeling verbreken'),
                    key: AccessKey.API_SETTINGS_DISCONNECT,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.setupApi', 'Koppeling instellen'),
                    key: AccessKey.API_SETTINGS_SETUP,
                    right_type: UserRightType.ACTION
                }
            ]
        },
        {
            label: i18n.t('util.access.rights.productsVat', 'Producten (BTW)'),
            key: AccessKey.PRODUCT_SETTINGS,
            right_type: UserRightType.ACTION,
            children: [
                {
                    label: i18n.t('util.access.rights.editProductTitle', 'Productnaam wijzigen'),
                    key: AccessKey.PRODUCT_SETTINGS_EDIT_TITLE,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.editVat', 'BTW% wijzigen'),
                    key: AccessKey.PRODUCT_SETTINGS_EDIT_VAT,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.syncBol', 'Sync met bol'),
                    key: AccessKey.PRODUCT_SETTINGS_SYNC,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.bulkUploadPercentages', 'Bulk percentages uploaden'),
                    key: AccessKey.PRODUCT_SETTINGS_UPLOAD,
                    right_type: UserRightType.ACTION
                }
            ]
        },
        {
            label: i18n.t('util.access.rights.invoiceSettings', 'Factuurinstellingen'),
            key: AccessKey.INVOICE_SETTINGS,
            right_type: UserRightType.ACTION,
            children: [
                {
                    label: i18n.t('util.access.rights.editSettings', 'Instellingen wijzigen'),
                    key: AccessKey.INVOICE_SETTINGS_EDIT,
                    right_type: UserRightType.ACTION
                }
            ]
        },
        {
            label: i18n.t('util.access.rights.emails', 'E-mails'),
            key: AccessKey.EMAIL_SETTINGS,
            right_type: UserRightType.ACTION,
            children: [
                {
                    label: i18n.t('util.access.rights.editSettings', 'Instellingen wijzigen'),
                    key: AccessKey.EMAIL_SETTINGS_EDIT,
                    right_type: UserRightType.ACTION
                }
            ]
        },
        {
            label: i18n.t('util.access.rights.export', 'Exporteren'),
            key: AccessKey.EXPORT_SETTINGS,
            right_type: UserRightType.ACTION,
            children: [
                {
                    label: i18n.t('util.access.rights.exportInvoices', 'Facturen exporteren'),
                    key: AccessKey.EXPORT_SETTINGS_EXPORT,
                    right_type: UserRightType.ACTION
                },
                {
                    label: i18n.t('util.access.rights.downloadExports', 'Exports downloaden'),
                    key: AccessKey.EXPORT_SETTINGS_DOWNLOAD,
                    right_type: UserRightType.ACTION
                }
            ]
        }
    ]
});
